import React, { useEffect, useState, useRef  } from 'react';
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import { StyledWrapper } from './Styled';
import { useNavigation } from '../../providers/NavigationProvider';
import { useAppState } from '../../providers/AppStateProvider';
import { useReactMediaRecorder } from 'react-media-recorder';
import { 
  getUploadUrl,
  saveEnTranscription
} from '../../utils/api';

const LiveTranscription = () => {
  const meetingManager = useMeetingManager();
  const { localUserName, setEnTranscriptions, enTranscriptions, clearEnTranscriptions } = useAppState();
  const [partialTranscript, setPartialTranscript] = useState<string>('');
  const captionEndRef = useRef<null | HTMLDivElement>(null);
  const { showCaption, showIndoCaption } = useNavigation();
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const { meetingId } = useAppState();
  const [shouldUpload, setShouldUpload] = useState(false);
  const [uploadTimestamp, setUploadTimestamp] = useState<number>();

  const { startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({
    audio: true,
    onStop: async (blobUrl: string, blob: Blob) => {
      try {
        console.log('Recording stopped:', blobUrl, blob);
        const timestamp = Date.now();
        setUploadTimestamp(timestamp)
        const bucketName = "sgh-chime-transcription";
        const keyName = `${meetingId}/${timestamp}-en-audio.wav`;
        const uploadUrl = await getUploadUrl(bucketName, keyName);
        if(uploadUrl){
          await fetch(uploadUrl, {
            method: "PUT",
            body: blob,
            headers: {
              "Content-Type": "audio/wav",
            },
          });
        }
      } catch (error) {
        console.error('Error during sending audio to s3:', error);
      }
    }
  });

  const handleStartRecording = async () => {
    startRecording();
    setIsRecordingStarted(true);
    console.log('Start recording');
  };

  const handleStopRecording = async () => {
    stopRecording();
    setIsRecordingStarted(false);
    setShouldUpload(true);
    console.log('Stop recording');
  };

  const uploadTranscriptions = async (timestamp: number) => {
    try {
      await saveEnTranscription(meetingId, enTranscriptions, timestamp);
      clearEnTranscriptions();
      setShouldUpload(false); // Reset flag
      setUploadTimestamp(undefined)
    } catch (error) {
      console.error('Error uploading transcription:', error);
    }
  };

  useEffect(() => {
    const audioVideo = meetingManager.audioVideo;
    if (!audioVideo?.transcriptionController) {
      console.error("TranscriptionController is not available.");
      return;
    }
    
    const handleTranscriptEvent = (event: any) => {
      if(showCaption && !showIndoCaption){
        const transcriptResults = event.results || [];
        transcriptResults.forEach((result: any) => {
          const transcriptText = result.alternatives[0]?.transcript || '';

          if (result.isPartial) {
            setPartialTranscript(transcriptText);
          }else {
            const dialogSegment = {
              attendee: localUserName,
              speech: [{
                content: transcriptText,
                start_time: Date.now(),
                end_time: Date.now() + 5000
              }]
            };
            setEnTranscriptions(dialogSegment);
          }
        });
      }
    };

    if(showCaption && !showIndoCaption){
      if (!isRecordingStarted) {
        handleStartRecording();
      }
    }else{
      setPartialTranscript('');
      if (isRecordingStarted) {
        handleStopRecording();
      }
    }

    // Subscribe to transcription events
    audioVideo.transcriptionController?.subscribeToTranscriptEvent(
      handleTranscriptEvent
    );
    

    return () => {
      // Unsubscribe from transcription events
      audioVideo.transcriptionController?.unsubscribeFromTranscriptEvent(
        handleTranscriptEvent
      );
    };
  }, [meetingManager, showCaption, showIndoCaption]);

  useEffect(() => {
    if (shouldUpload && enTranscriptions.length > 0 && uploadTimestamp) {
      uploadTranscriptions(uploadTimestamp);
    }
  }, [enTranscriptions, shouldUpload, uploadTimestamp]);

  useEffect(() => {
    captionEndRef.current?.scrollIntoView({ behavior: "smooth" })
  });

  return (
    <StyledWrapper active={!!showCaption}>
      <div className="caption-container">
        {partialTranscript && <span className="partial-transcript">{partialTranscript}</span>}
        <div ref={captionEndRef} />
      </div>
    </StyledWrapper>
    
    
  );
};

export default LiveTranscription;